import React from 'react';
import { useEntity, EntityTable, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { RELATION_HAS_PART, ComponentEntity } from '@backstage/catalog-model';
import { InfoCard, InfoCardVariants, Progress } from '@backstage/core-components';
import { Typography } from '@material-ui/core';

type Props = {
  variant?: InfoCardVariants;
};

export const CustomEntityHasSubcomponentsCard = ({ variant = 'gridItem' }: Props) => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [subcomponents, setSubcomponents] = React.useState<ComponentEntity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

  React.useEffect(() => {
    let mounted = true;
    
    const fetchSubcomponents = async () => {
      setLoading(true);
      
      try {
        const subcomponentRefs = (entity.relations ?? [])
          .filter(r => r.type === RELATION_HAS_PART)
          .map(r => r.targetRef);

        if (subcomponentRefs.length === 0) {
          if (mounted) {
            setSubcomponents([]);
            setLoading(false);
          }
          return;
        }


        const { items } = await catalogApi.getEntities({
          filter: {
            'metadata.name': subcomponentRefs.map(ref => ref.split('/').pop() || '')
          },
        });
        
        if (mounted) {
          setSubcomponents(items as ComponentEntity[]);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch subcomponents', error);
        if (mounted) {
          setSubcomponents([]);
          setLoading(false);
        }
      }
    };

    fetchSubcomponents();
    
    return () => {
      mounted = false;
    };
  }, [entityRef, catalogApi]);

  if (loading) {
    return (
      <InfoCard title="Subcomponents" variant={variant}>
        <Progress />
      </InfoCard>
    );
  }

  return (
    <EntityTable
      title="Subcomponents"
      variant={variant}
      columns={EntityTable.componentEntityColumns}
      tableOptions={{
        search: true,
        paging: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 20, 50],
      }}
      entities={subcomponents}
      emptyContent={
        <Typography variant="body2" color="textSecondary">
          No subcomponents (Component/Screen) found.
        </Typography>
      }
    />
  );
};
